import client from "./client";
import { cacheDelete, TTL, userCacheKey } from "../lib/cache";
import { fetchCached } from "./fetchCached";

export const ORGANIZATION_KEY = (user) => userCacheKey(user, "organization");
export const ORGANIZATION_MEMBERS_KEY = (user) => userCacheKey(user, "organization:members");
export const ORGANIZATION_COLLEAGUES_KEY = (user) => userCacheKey(user, "organization:colleagues");
export const ORGANIZATION_LICENSE_KEY = (user) => userCacheKey(user, "organization:license");

export function invalidateOrganization(user) {
  cacheDelete(ORGANIZATION_KEY(user));
  cacheDelete(ORGANIZATION_MEMBERS_KEY(user));
  cacheDelete(ORGANIZATION_COLLEAGUES_KEY(user));
  cacheDelete(ORGANIZATION_LICENSE_KEY(user));
}

/**
 * @param {object|null} user
 * @param {{ revalidate?: boolean }} [opts]
 */
export async function getOrganization(user, { revalidate = false } = {}) {
  const key = ORGANIZATION_KEY(user);
  const { data } = await fetchCached({
    key,
    ttl: TTL.ORGANIZATION,
    revalidate,
    fetcher: async () => {
      const res = await client.get("/me/organization");
      return res.data;
    },
  });
  return data;
}

/**
 * @param {object|null} user
 * @param {{ revalidate?: boolean }} [opts]
 */
export async function listOrganizationMembers(user, { revalidate = false } = {}) {
  const key = ORGANIZATION_MEMBERS_KEY(user);
  const { data } = await fetchCached({
    key,
    ttl: TTL.ORGANIZATION,
    revalidate,
    fetcher: async () => {
      const res = await client.get("/me/organization/members");
      return res.data;
    },
  });
  return data;
}

/**
 * Registers (or activates) a backend account from an org invite, using the
 * Supabase access token obtained by the invite page's own signUp/signIn call
 * — the caller is not yet cookie-authenticated, so this bypasses `client`'s
 * default credentials and passes the bearer token explicitly. Idempotent on
 * the backend: safe to call again after an email-confirmation round trip.
 *
 * @param {string} token
 * @param {{ name?: string }} payload
 * @param {string} supabaseAccessToken
 */
export async function registerViaInvite(token, payload, supabaseAccessToken) {
  const { data } = await client.post(`/invites/${encodeURIComponent(token)}/register`, payload, {
    headers: { Authorization: `Bearer ${supabaseAccessToken}` },
  });
  return data;
}

/**
 * @param {object|null} user
 * @param {{ revalidate?: boolean }} [opts]
 */
export async function getMyLicense(user, { revalidate = false } = {}) {
  const key = ORGANIZATION_LICENSE_KEY(user);
  const { data } = await fetchCached({
    key,
    ttl: TTL.ORGANIZATION,
    revalidate,
    fetcher: async () => {
      const res = await client.get("/me/organization/license");
      return res.data;
    },
  });
  return data;
}

/**
 * @param {object|null} user
 * @param {{ revalidate?: boolean }} [opts]
 */
export async function listOrganizationColleagues(user, { revalidate = false } = {}) {
  const key = ORGANIZATION_COLLEAGUES_KEY(user);
  const { data } = await fetchCached({
    key,
    ttl: TTL.ORGANIZATION,
    revalidate,
    fetcher: async () => {
      const res = await client.get("/me/organization/colleagues");
      return res.data;
    },
  });
  return data;
}
